import React from "react";
import { useIsMobile } from "../../hooks/useIsMobile";

interface Props {
  label: string;
  value: React.ReactNode;
  sub?: string;
  subNode?: React.ReactNode;
  accent?: boolean;
  success?: boolean;
  danger?: boolean;
  locked?: boolean;
  mobileHide?: boolean;
  /** Raw CSS colour (e.g. "var(--color-accent)") — overrides the top border and value colour. Takes priority over accent/success/danger, but not locked. */
  color?: string;
  /** Soft background + border tint in this colour, independent of the value's text colour (which still follows accent/success/danger/color/locked). Distinct from `color`, which instead draws a top border. */
  borderColor?: string;
  /** sub/subNode are hidden on mobile by default (most callers stack 4+ boxes at 25% width, too tight for subtext). Opt in per box when the mobile layout has room. */
  showSubOnMobile?: boolean;
  /** Label is single-line ellipsis-truncated by default (built for wide 4-across rows). Opt in to let it wrap instead, for narrower contexts with long labels. */
  wrapLabel?: boolean;
}

export default function StatBox({ label, value, sub, subNode, accent, success, danger, locked, mobileHide, color, borderColor, showSubOnMobile, wrapLabel }: Props) {
  const isMobile = useIsMobile();
  if (isMobile && mobileHide) return null;
  const valueColor = locked
    ? "var(--color-text-dim)"
    : color
    ? color
    : danger
    ? "var(--color-danger)"
    : success
    ? "var(--color-success)"
    : accent
    ? "var(--color-accent)"
    : "var(--color-text)";
  const showSub = !locked && (!isMobile || showSubOnMobile);

  return (
    <div style={{
      flex: "1 1 0",
      // 2-up on mobile, not the 4-across that desktop's wider boxes use — at ~70px a box's
      // label was already ellipsised down to nothing (see StatBox.test.tsx).
      minWidth: isMobile ? "calc(50% - 0.5rem)" : 0,
      background: borderColor
        ? `color-mix(in srgb, ${borderColor} 6%, var(--color-surface-alt))`
        : "var(--color-surface-alt)",
      border: `1px solid ${borderColor ? `color-mix(in srgb, ${borderColor} 18%, var(--color-border-subtle))` : "var(--color-border-subtle)"}`,
      ...(color ? { borderTop: `3px solid ${color}` } : {}),
      borderRadius: 10,
      padding: isMobile ? "0.6rem 0.75rem" : "0.85rem 1rem",
      display: "flex",
      flexDirection: "column",
    }}>
      <div style={{
        fontSize: "0.6rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        color: "var(--color-text-dim)",
        marginBottom: "0.5rem",
        ...(wrapLabel
          ? { whiteSpace: "normal" as const, wordBreak: "break-word" as const }
          : { whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" }),
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: "var(--font-display)",
        fontSize: locked ? (isMobile ? "1.1rem" : "1.4rem") : isMobile ? "1.2rem" : "2rem",
        fontWeight: 700,
        lineHeight: 1,
        color: valueColor,
        opacity: locked ? 0.35 : 1,
        overflow: "hidden",
        wordBreak: "break-word",
      }}>
        {locked ? "N/A" : value}
      </div>
      {sub && showSub && (
        <div style={{
          fontSize: "0.7rem",
          color: "var(--color-text-dim)",
          marginTop: "0.35rem",
          fontFamily: "var(--font-body)",
        }}>
          {sub}
        </div>
      )}
      {subNode && showSub && (
        <div style={{ marginTop: "0.35rem", fontFamily: "var(--font-body)" }}>
          {subNode}
        </div>
      )}
    </div>
  );
}
